import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Trash2, Printer, AlertTriangle, CheckCircle, Loader2,
  Download, RotateCcw, Mail, MailX, X, Phone, MapPin,
  ShoppingBag, Bike, ChefHat, Receipt,
  Check, ChefHat as ChefIcon, PackageCheck, Truck, CircleSlash,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  AdminMobileShell, AdminPageContainer, PrintQueueBadge,
} from '../../components/admin';
import { useAuth } from '../../context/AuthContext';
import { orderAPI } from '../../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import {
  loadQZTray, connectQZ, isQZConnected,
} from '../../utils/qzPrinter';
import printerService from '../../services/printer/printerService';
import { markPrintedLocally } from '../../utils/autoPrint';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ----------------------------------------------------------------------- */
/* Helpers                                                                  */
/* ----------------------------------------------------------------------- */

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

const orderTotalNum = (o) => Number(o.total_amount || 0);

const orderNumberLabel = (o) =>
  o.daily_order_number
    ? `#${String(o.daily_order_number).padStart(3, '0')}`
    : `#${(o.id || '').slice(-6).toUpperCase()}`;

const itemCount = (o) =>
  (o.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);

/** Compute the bucket (today / yesterday / history) of an order in UK time. */
const computeBucket = (order, ukNow = new Date()) => {
  const created = new Date(order.created_at || order.order_date || ukNow);
  // Convert both dates to UK timezone date strings (YYYY-MM-DD) for comparison
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  });
  const orderStr = fmt.format(created);
  const todayStr = fmt.format(ukNow);
  const yesterday = new Date(ukNow.getTime() - 24 * 60 * 60 * 1000);
  const yesterdayStr = fmt.format(yesterday);
  if (orderStr === todayStr) return 'today';
  if (orderStr === yesterdayStr) return 'yesterday';
  return 'history';
};

const orderTime = (o) =>
  new Date(o.created_at).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
  });

const orderDate = (o) =>
  new Date(o.created_at).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London',
  });

/** Backend status value → UI label. */
const STATUS_LABEL = {
  pending: 'New',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const STATUS_OPTIONS = [
  'pending', 'received', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'completed', 'cancelled',
];

const STATUS_STYLE = {
  pending: 'bg-red-600 text-white',
  received: 'bg-amber-100 text-amber-900',
  confirmed: 'bg-stone-200 text-stone-800',
  preparing: 'bg-amber-100 text-amber-800',
  ready: 'bg-green-100 text-green-800',
  out_for_delivery: 'bg-green-100 text-green-800',
  completed: 'bg-green-600 text-white',
  cancelled: 'bg-stone-200 text-stone-500 line-through',
};

const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'history', label: 'History' },
];

/* ----------------------------------------------------------------------- */
/* Quick-status action buttons (touch-friendly, used on phone + POS)        */
/* ----------------------------------------------------------------------- */

const QUICK_STATUS_FLOW = [
  { value: 'confirmed',        label: 'Accept',     short: 'Accept',     icon: Check },
  { value: 'preparing',        label: 'Preparing',  short: 'Prep',       icon: ChefIcon },
  { value: 'ready',            label: 'Ready',      short: 'Ready',      icon: PackageCheck },
  { value: 'out_for_delivery', label: 'Out',        short: 'Out',        icon: Truck, deliveryOnly: true },
  { value: 'completed',        label: 'Complete',   short: 'Done',       icon: CheckCircle },
  { value: 'cancelled',        label: 'Cancel',     short: 'Cancel',     icon: CircleSlash, destructive: true },
];

const QuickStatusButtons = ({ currentStatus, disabled, isDelivery, onChange }) => {
  const buttons = QUICK_STATUS_FLOW.filter(
    (b) => !b.deliveryOnly || isDelivery
  );
  return (
    <div
      className="grid grid-cols-3 gap-2 sm:grid-cols-6"
      data-testid="quick-status-buttons"
    >
      {buttons.map((b) => {
        const active = currentStatus === b.value;
        const Icon = b.icon;
        return (
          <button
            key={b.value}
            type="button"
            onClick={() => onChange(b.value)}
            disabled={disabled || active}
            data-testid={`quick-status-${b.value}`}
            className={[
              'flex h-14 flex-col items-center justify-center gap-0.5 rounded-xl border-2 px-2 text-xs font-semibold transition-all active:scale-95',
              active
                ? 'border-stone-900 bg-stone-900 text-white cursor-default'
                : b.destructive
                  ? 'border-red-200 bg-white text-red-700 hover:bg-red-50'
                  : 'border-stone-200 bg-white text-stone-800 hover:border-stone-400 hover:bg-stone-50',
              disabled && !active ? 'opacity-50' : '',
            ].join(' ')}
          >
            <Icon className="h-4 w-4" />
            <span className="leading-tight">{b.short}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* CSV export                                                               */
/* ----------------------------------------------------------------------- */

const exportOrdersToCSV = (orders, filename = 'orders.csv') => {
  const headers = [
    'Order #', 'Date', 'Time', 'Customer Name', 'Phone', 'Email',
    'Order Type', 'Address', 'Postcode', 'Items', 'Total (£)',
    'Payment Method', 'Payment Status', 'Order Status', 'Notes',
  ];
  const rows = orders.map((order) => {
    const d = new Date(order.created_at || order.uk_order_time);
    const items = (order.items || []).map((i) => `${i.quantity}x ${i.name}`).join('; ');
    return [
      orderNumberLabel(order),
      d.toLocaleDateString('en-GB'),
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }),
      order.customer_name || '',
      order.customer_phone || '',
      order.customer_email || '',
      order.order_type || '',
      order.delivery_address?.replace(/\n/g, ', ') || '',
      order.postcode || '',
      items,
      order.total_amount?.toFixed(2) || '0.00',
      order.payment_method || 'card',
      order.payment_status || '',
      order.order_status || '',
      order.notes?.replace(/\n/g, ' ') || '',
    ];
  });
  const escapeCSV = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[,"\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers, ...rows].map((r) => r.map(escapeCSV).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/* ----------------------------------------------------------------------- */
/* Small presentational components                                          */
/* ----------------------------------------------------------------------- */

const StatusPill = ({ status, big }) => {
  const cls = STATUS_STYLE[status] || 'bg-stone-200 text-stone-800';
  const sizing = big ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  return (
    <span className={`inline-flex items-center rounded-full font-semibold ${sizing} ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
};

const TypeChip = ({ type }) => {
  const isDelivery = (type || '').toLowerCase() === 'delivery';
  const Icon = isDelivery ? Bike : ShoppingBag;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2.5 py-1 text-xs font-medium text-stone-700">
      <Icon className="h-3.5 w-3.5" />
      {isDelivery ? 'Delivery' : 'Collection'}
    </span>
  );
};

/* ----------------------------------------------------------------------- */
/* Order list row                                                           */
/* ----------------------------------------------------------------------- */

const OrderRow = ({ order, selected, isNew, onOpen, onPrint, printing }) => {
  const isDelivery = (order.order_type || '').toLowerCase() === 'delivery';
  const total = orderTotalNum(order);
  const count = itemCount(order);
  const printFailed = order.print_status === 'FAILED';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(order.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(order.id); }
      }}
      data-testid={`order-row-${order.id}`}
      className={`order-row relative w-full cursor-pointer overflow-hidden rounded-xl border text-left transition-colors ${
        selected
          ? 'border-stone-900 bg-stone-50'
          : printFailed
            ? 'border-amber-500 bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-500'
            : isNew
              ? 'border-red-200 bg-red-50/70 hover:bg-red-50'
              : 'border-stone-200 bg-white hover:bg-stone-50'
      }`}
    >
      {(isNew || printFailed) && (
        <span
          aria-hidden
          className={`new-bar absolute left-0 top-0 h-full w-1 ${printFailed ? 'bg-amber-600' : 'bg-red-600'}`}
        />
      )}
      <div className="px-4 py-4 pl-5 sm:py-3">
        <div className="flex items-baseline justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="mono text-lg font-bold text-stone-900 sm:text-base">
              {orderNumberLabel(order)}
            </span>
            {isNew && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-white" />
                New
              </span>
            )}
            {printFailed && (
              <span
                data-testid={`order-row-print-failed-${order.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
              >
                <Printer className="h-3 w-3" />
                Not printed
              </span>
            )}
            {order.is_preorder && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-orange-800">
                Pre
              </span>
            )}
          </div>
          <span className="mono shrink-0 text-base font-bold text-stone-900 sm:text-sm">
            {gbp(total)}
          </span>
        </div>

        <div className="mt-1 truncate text-base text-stone-700 sm:text-sm">
          {order.customer_name || 'Guest'}
        </div>

        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-stone-500">
          {isDelivery ? <Bike className="h-3.5 w-3.5" /> : <ShoppingBag className="h-3.5 w-3.5" />}
          <span>{count} item{count !== 1 ? 's' : ''}</span>
          <span>·</span>
          <span className="truncate">
            {isDelivery && order.postcode ? order.postcode : 'Collection'}
          </span>
          <span>·</span>
          <span
            data-testid={`order-row-payment-${order.id}`}
            className="shrink-0 font-bold uppercase tracking-wide text-red-600"
          >
            {order.payment_method === 'cod'
              ? 'COD'
              : order.payment_method === 'coc'
                ? 'COC'
                : 'STRIPE'}
          </span>
          <span className="ml-auto whitespace-nowrap">{orderTime(order)}</span>
        </div>

        {printFailed && onPrint && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-amber-100/70 px-2 py-1.5 text-xs">
            <span className="truncate text-amber-900">
              Auto-print failed{order.printer_error ? ` — ${order.printer_error}` : ''}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onPrint(order);
              }}
              disabled={printing === order.id}
              data-testid={`order-row-print-now-${order.id}`}
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
            >
              <Printer className="h-3 w-3" />
              {printing === order.id ? 'Printing…' : 'Print now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Item line with modifiers indented                                        */
/* ----------------------------------------------------------------------- */

const ItemLine = ({ item }) => {
  // Customizations can be: { key: value }, { key: [values] }, with each value
  // either a plain string or { name, price }. We render them all the same way.
  const flattenMods = () => {
    const cust = item.customizations || {};
    const out = [];
    for (const value of Object.values(cust)) {
      const arr = Array.isArray(value) ? value : [value];
      for (const v of arr) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'object') {
          out.push({ name: v.name || String(v), price: Number(v.price || 0) });
        } else {
          out.push({ name: String(v), price: 0 });
        }
      }
    }
    return out;
  };
  const mods = flattenMods();
  return (
    <div className="border-b border-dashed border-stone-200 pb-2 last:border-0 last:pb-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className="mono text-sm font-semibold text-stone-900">
          {item.quantity}× {item.name}
        </div>
        <div className="mono shrink-0 text-sm font-semibold text-stone-900">
          {gbp(Number(item.price || 0) * Number(item.quantity || 1))}
        </div>
      </div>
      {mods.length > 0 && (
        <ul className="mt-1 space-y-0.5 pl-4 text-xs text-stone-600">
          {mods.map((m, j) => (
            <li key={m.id || m.name || `mod-${j}`} className="flex items-baseline justify-between gap-2">
              <span>+ {m.name}</span>
              {m.price > 0 && (
                <span className="mono shrink-0 text-stone-500">
                  {gbp(m.price * Number(item.quantity || 1))}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Order detail — the kitchen "ticket"                                      */
/* ----------------------------------------------------------------------- */

const OrderDetail = ({
  order, currentStatus, onStatus, statusUpdating,
  onPrint, printing, printerConfigured,
  onResendEmail, resendingEmail,
  onMarkPaid, markingPaid,
  onRefund, onDelete, onClose,
}) => {
  const subtotal = Number(order.subtotal ?? (orderTotalNum(order) - Number(order.delivery_fee || 0) - Number(order.service_charge || 0) - Number(order.tip || 0))).toFixed(2);
  const isDelivery = (order.order_type || '').toLowerCase() === 'delivery';
  const status = currentStatus || order.order_status;
  const total = orderTotalNum(order);
  const refunded = Number(order.refunded_amount || 0);
  const paymentStatus = (order.payment_status || '').toUpperCase();
  const isCash = ['cod', 'coc'].includes((order.payment_method || '').toLowerCase());
  const printStatus = order.print_status || 'NOT_PRINTED';
  const refundable = total - refunded;
  const canRefund =
    refundable > 0.005 &&
    ['PAID', 'SUCCEEDED', 'COMPLETED', 'PARTIALLY_REFUNDED'].includes(paymentStatus);

  return (
    <div className="flex h-full flex-col bg-stone-100">
      {/* Header (back / close) */}
      <div className="flex items-center justify-between border-b border-stone-200 bg-white px-4 py-3 lg:px-6">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium text-stone-700 hover:bg-stone-100 lg:hidden"
          data-testid="order-detail-close"
        >
          <ArrowLeft className="h-4 w-4" />
          All orders
        </button>
        <div className="hidden text-sm font-medium text-stone-500 lg:block">
          Order ticket
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => onPrint(order)}
            disabled={printing === order.id || !printerConfigured}
            className="bg-stone-900 text-white hover:bg-stone-800"
            data-testid="reprint-button"
          >
            {printing === order.id ? (
              <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Printing…</>
            ) : (
              <>
                <Printer className="mr-1.5 h-4 w-4" />
                {printStatus === 'PRINTED' ? 'Reprint' : 'Print'}
              </>
            )}
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="hidden rounded-md p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-900 lg:inline-flex"
            aria-label="Close ticket"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Scrollable docket */}
      <div className="flex-1 overflow-y-auto px-4 py-5 lg:px-6">
        <div className="mx-auto max-w-2xl space-y-5">
          {/* Brand strip + dashed receipt top */}
          <div className="rounded-2xl bg-white shadow-sm">
            <div className="h-1.5 rounded-t-2xl bg-red-600" />
            <div className="border-b border-dashed border-stone-300 px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-stone-500">Order</p>
                  <p className="mono text-3xl font-black tracking-tight text-stone-900">
                    {orderNumberLabel(order)}
                  </p>
                  <p className="mt-1 text-xs text-stone-500">
                    {orderDate(order)} · {orderTime(order)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <TypeChip type={order.order_type} />
                  <StatusPill status={status} big />
                </div>
              </div>
            </div>

            {/* Quick-status action buttons (touch-friendly) */}
            <div className="px-3 pt-4 sm:px-5">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Quick actions
              </p>
              <QuickStatusButtons
                currentStatus={status}
                disabled={statusUpdating}
                isDelivery={isDelivery}
                onChange={onStatus}
              />
            </div>

            {/* Status dropdown (covers cases not in quick-actions, e.g. revert) */}
            <div className="px-3 py-4 sm:px-5">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-stone-500">
                Set status manually
              </p>
              <Select
                value={status}
                onValueChange={onStatus}
                disabled={statusUpdating}
              >
                <SelectTrigger
                  data-testid="order-status-select"
                  className="mono h-12 font-semibold"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Customer block */}
            <div className="border-t border-dashed border-stone-200 px-5 py-4">
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-stone-500">
                Customer
              </p>
              <p className="text-base font-semibold text-stone-900">
                {order.customer_name || 'Guest'}
              </p>
              {order.customer_phone && (
                <a
                  href={`tel:${order.customer_phone}`}
                  className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-red-700 hover:underline"
                  data-testid="customer-phone"
                >
                  <Phone className="h-3.5 w-3.5" />
                  {order.customer_phone}
                </a>
              )}
              {order.customer_email && (
                <p className="mt-0.5 text-sm text-stone-600 break-all">
                  {order.customer_email}
                </p>
              )}
              {isDelivery && order.delivery_address && (
                <div className="mt-3 flex items-start gap-1.5 text-sm text-stone-700">
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-stone-400" />
                  <span>
                    {order.delivery_address}
                    {order.postcode ? ` (${order.postcode})` : ''}
                  </span>
                </div>
              )}
            </div>

            {/* Items */}
            <div className="border-t border-dashed border-stone-200 px-5 py-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-stone-500">
                Items
              </p>
              <div className="space-y-2.5">
                {(order.items || []).map((item, i) => (
                  <ItemLine key={item.id || `${item.name || 'item'}-${i}`} item={item} />
                ))}
              </div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="border-t border-dashed border-stone-200 px-5 py-4">
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-amber-900">
                    <ChefHat className="h-3.5 w-3.5" />
                    Kitchen note
                  </div>
                  <p className="mt-1 text-sm text-amber-900">{order.notes}</p>
                </div>
              </div>
            )}

            {/* Totals */}
            <div className="border-t border-dashed border-stone-200 px-5 py-4">
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between text-stone-600">
                  <dt>Subtotal</dt>
                  <dd className="mono">{gbp(subtotal)}</dd>
                </div>
                {Number(order.delivery_fee || 0) > 0 && (
                  <div className="flex justify-between text-stone-600">
                    <dt>Delivery</dt>
                    <dd className="mono">{gbp(order.delivery_fee)}</dd>
                  </div>
                )}
                {Number(order.service_charge || 0) > 0 && (
                  <div className="flex justify-between text-stone-600">
                    <dt>Service charge</dt>
                    <dd className="mono">{gbp(order.service_charge)}</dd>
                  </div>
                )}
                {Number(order.tip || 0) > 0 && (
                  <div
                    className="flex justify-between text-emerald-700"
                    data-testid="order-tip-amount"
                  >
                    <dt>Tip</dt>
                    <dd className="mono">{gbp(order.tip)}</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-stone-200 pt-2 text-base font-bold text-stone-900">
                  <dt>Total</dt>
                  <dd className="mono">{gbp(total)}</dd>
                </div>
                {refunded > 0 && (
                  <div
                    className="flex justify-between text-red-700"
                    data-testid={`refund-badge-${order.id}`}
                  >
                    <dt>Refunded</dt>
                    <dd className="mono">−{gbp(refunded)}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Payment / fulfilment tags */}
            <div className="flex flex-wrap items-center gap-2 border-t border-dashed border-stone-200 px-5 py-4">
              {paymentStatus === 'COD_PENDING' ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
                  {order.payment_method === 'coc' ? 'Cash on Collection' : 'Cash on Delivery'}
                </span>
              ) : (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    ['PAID', 'SUCCEEDED', 'COMPLETED'].includes(paymentStatus)
                      ? 'bg-green-100 text-green-800'
                      : ['REFUNDED', 'PARTIALLY_REFUNDED'].includes(paymentStatus)
                        ? 'bg-red-100 text-red-700'
                        : 'bg-stone-100 text-stone-700'
                  }`}
                >
                  {paymentStatus || 'Unknown'}
                </span>
              )}
              {isCash && (
                <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold uppercase text-stone-600">
                  {order.payment_method === 'coc' ? 'COC' : 'COD'}
                </span>
              )}
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                printStatus === 'PRINTED'
                  ? 'bg-green-100 text-green-800'
                  : printStatus === 'FAILED'
                    ? 'bg-red-100 text-red-700'
                    : printStatus === 'PRINTING'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-stone-100 text-stone-700'
              }`}>
                <Printer className="h-3 w-3" />
                {printStatus === 'PRINTED' ? 'Printed'
                  : printStatus === 'FAILED' ? 'Print failed'
                  : printStatus === 'PRINTING' ? 'Printing…'
                  : 'Not printed'}
              </span>
              {order.email_status === 'sent' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                  <Mail className="h-3 w-3" /> Email sent
                </span>
              )}
              {order.email_status === 'failed' && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700"
                  title={order.email_error || ''}
                >
                  <MailX className="h-3 w-3" /> Email failed
                </span>
              )}
              {order.email_status === 'skipped_no_email' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                  <MailX className="h-3 w-3" /> No email
                </span>
              )}
              {order.is_preorder && (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold text-orange-800">
                  Preorder
                </span>
              )}
              {printStatus === 'FAILED' && order.printer_error && (
                <p className="basis-full text-xs text-red-700">{order.printer_error}</p>
              )}
            </div>
          </div>

          {/* Inline mark-paid for cash orders */}
          {isCash && paymentStatus === 'COD_PENDING' && (
            <Button
              type="button"
              onClick={() => onMarkPaid(order.id)}
              disabled={markingPaid === order.id}
              className="w-full bg-green-600 text-white hover:bg-green-700"
              data-testid="mark-paid-button"
            >
              {markingPaid === order.id ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Marking paid…</>
              ) : (
                <><CheckCircle className="mr-2 h-4 w-4" /> Mark as paid</>
              )}
            </Button>
          )}

          {/* Delete (only for completed / cancelled) */}
          {['completed', 'cancelled'].includes(status) && (
            <Button
              type="button"
              onClick={() => onDelete(order.id, status)}
              variant="outline"
              className="w-full border-red-200 text-red-700 hover:bg-red-50"
              data-testid="delete-order-button"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete order
            </Button>
          )}
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="border-t border-stone-200 bg-white px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:px-6">
        <div className="mx-auto flex max-w-2xl flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onResendEmail(order)}
            disabled={resendingEmail === order.id}
            data-testid={`resend-email-${order.id}`}
            className="h-12 flex-1 text-base font-semibold"
          >
            {resendingEmail === order.id ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
            ) : (
              <><Mail className="mr-2 h-4 w-4" /> Resend email</>
            )}
          </Button>
          {canRefund && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onRefund(order)}
              className="h-12 flex-1 border-red-200 text-base font-semibold text-red-700 hover:bg-red-50"
              data-testid={`refund-button-${order.id}`}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Refund
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------------- */
/* Empty state                                                              */
/* ----------------------------------------------------------------------- */

const EmptyDetail = () => (
  <div className="flex h-full items-center justify-center bg-stone-50 px-6 text-center">
    <div className="max-w-sm">
      <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-stone-100 text-stone-400">
        <Receipt className="h-6 w-6" />
      </div>
      <p className="text-base font-semibold text-stone-900">
        Select an order to open the ticket
      </p>
      <p className="mt-1 text-sm text-stone-500">
        New orders are flagged red until you open them.
      </p>
    </div>
  </div>
);

/* ----------------------------------------------------------------------- */
/* Main                                                                     */
/* ----------------------------------------------------------------------- */

const AdminOrders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [refunding, setRefunding] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [qzConnected, setQzConnected] = useState(false);
  const [printerSettings, setPrinterSettings] = useState(null);

  // New UI state for master-detail
  const [tab, setTab] = useState('today');
  const [selectedId, setSelectedId] = useState(null);
  const [viewed, setViewed] = useState(() => new Set());

  const printedOrdersRef = useRef(new Set());

  // ------- Bucketing + counts ---------------------------------------------

  const ordersWithBuckets = useMemo(
    () => orders.map((o) => ({ ...o, _bucket: computeBucket(o) })),
    [orders]
  );

  const isUnopenedNew = useCallback(
    (o) => o.order_status === 'pending' && !viewed.has(o.id),
    [viewed]
  );

  const counts = useMemo(() => {
    const c = { today: 0, yesterday: 0, history: 0, newToday: 0 };
    ordersWithBuckets.forEach((o) => {
      c[o._bucket] += 1;
      if (o._bucket === 'today' && isUnopenedNew(o)) c.newToday += 1;
    });
    return c;
  }, [ordersWithBuckets, isUnopenedNew]);

  const visibleOrders = useMemo(() => {
    return ordersWithBuckets
      .filter((o) => o._bucket === tab)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [ordersWithBuckets, tab]);

  const selected = useMemo(
    () => orders.find((o) => o.id === selectedId) || null,
    [orders, selectedId]
  );

  const openOrder = useCallback((id) => {
    setSelectedId(id);
    setViewed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // ------- Printer settings + QZ -------------------------------------------

  const loadPrinterSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/printer-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrinterSettings(response.data);
      // Sync the backend-authoritative mode into printerService so any
      // downstream caller (queue retry, manual print) picks the right adapter
      // even if the operator never visited /admin/printer-settings this session.
      if (response.data?.printer_mode) {
        printerService.setMode(response.data.printer_mode);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to load printer settings:', error);
      return null;
    }
  }, []);

  // Initialize QZ Tray connection lazily.
  // SAFE TO CALL REPEATEDLY: connectQZ() is a singleton that no-ops when
  // already connected. We never call it on every render to avoid spamming
  // the QZ Tray "Allow/Block" permission popup.
  const initQZTray = useCallback(async () => {
    try {
      await loadQZTray();
      await connectQZ();
      setQzConnected(true);
      return true;
    } catch (error) {
      console.error('QZ Tray not available:', error);
      setQzConnected(false);
      return false;
    }
  }, []);

  // Auto-printing is handled globally by autoPrintController (mounted from
  // NotificationCenter). This page is responsible only for reflecting the
  // resulting print_status changes in the orders list — which happens
  // naturally on the next fetchOrders() tick below.

  // ------- Data loading ----------------------------------------------------

  const fetchOrders = useCallback(async () => {
    try {
      const response = await orderAPI.getAllOrders();
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }

    let cancelled = false;
    const init = async () => {
      await loadPrinterSettings();
      await fetchOrders();
      const pollInterval = setInterval(async () => {
        if (cancelled) return;
        const response = await orderAPI.getAllOrders().catch(() => null);
        if (response?.data) {
          setOrders(response.data);
        }
      }, 10000);
      return () => clearInterval(pollInterval);
    };
    const cleanup = init();
    return () => {
      cancelled = true;
      cleanup.then?.((fn) => fn && fn());
    };
  }, [user, navigate, loadPrinterSettings, fetchOrders]);

  // ------- Mutations -------------------------------------------------------

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    try {
      await orderAPI.updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, order_status: newStatus } : o))
      );
      toast.success('Order status updated');
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdating(null);
    }
  };

  // Manual print — routes through the shared printerService (hard-wired to
  // Android built-in POS printer).
  const handleQZPrint = async (order) => {
    setPrinting(order.id);
    const result = await printerService.printOrder(order);
    if (result.success) {
      markPrintedLocally(order.id);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/qz-print`,
        { success: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Receipt printed!');
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? { ...o, print_status: 'PRINTED', printed_at: new Date().toISOString(), printer_error: null }
            : o
        )
      );
    } else {
      const errMsg = result.error || 'Print failed';
      console.error('Print failed:', errMsg);
      toast.error(`Print failed: ${errMsg}`);
      const token = localStorage.getItem('token');
      await axios
        .post(
          `${API_URL}/api/admin/orders/${order.id}/qz-print`,
          { success: false, error: errMsg },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        .catch(() => {});
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id ? { ...o, print_status: 'FAILED', printer_error: errMsg } : o
        )
      );
    }
    setPrinting(null);
  };

  const handleDeleteOrder = async (orderId, orderStatus) => {
    if (!['completed', 'cancelled'].includes(orderStatus)) {
      toast.error('Can only delete completed or cancelled orders');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }
    try {
      await orderAPI.deleteOrder(orderId);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      if (selectedId === orderId) setSelectedId(null);
      toast.success('Order deleted successfully');
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const handleMarkCodPaid = async (orderId) => {
    setMarkingPaid(orderId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/orders/${orderId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId
            ? { ...o, payment_status: 'PAID', paid_at: new Date().toISOString() }
            : o
        )
      );
      toast.success('Order marked as paid');
    } catch (error) {
      console.error('Failed to mark order as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark order as paid');
    } finally {
      setMarkingPaid(null);
    }
  };

  // Resend confirmation email. Admin can override the destination.
  const handleResendEmail = async (order) => {
    const current = order.customer_email || '';
    const next = window.prompt('Resend confirmation email to:', current);
    if (next === null) return;
    setResendingEmail(order.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/resend-email`,
        { email: next && next !== current ? next : undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((curr) =>
        curr.map((o) =>
          o.id === order.id
            ? {
                ...o,
                customer_email: r.data.sent_to || o.customer_email,
                email_status: 'sent',
                email_provider_id: r.data.provider_id,
              }
            : o
        )
      );
      toast.success(`Email sent to ${r.data.sent_to}`);
    } catch (error) {
      console.error('Resend failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResendingEmail(null);
    }
  };

  // Refund flow shared for card + cash, with optional 4-digit passcode.
  const handleRefundOrder = async (order, { amount, reason, passcode }) => {
    setRefunding(order.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_URL}/api/admin/orders/${order.id}/refund`,
        { amount: amount ?? null, reason: reason || null, passcode: passcode || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((curr) =>
        curr.map((o) =>
          o.id === order.id
            ? {
                ...o,
                payment_status: r.data.new_status,
                refunded_amount: r.data.refunded_amount_total,
                refunds: [...(o.refunds || []), r.data.refund],
              }
            : o
        )
      );
      toast.success(
        r.data.new_status === 'REFUNDED'
          ? `Refunded £${r.data.refund.amount.toFixed(2)} — order fully refunded`
          : `Refunded £${r.data.refund.amount.toFixed(2)} (partial)`
      );
      setRefundTarget(null);
    } catch (error) {
      console.error('Refund failed:', error);
      toast.error(error.response?.data?.detail || 'Refund failed');
    } finally {
      setRefunding(null);
    }
  };

  if (!user || !user.is_admin) return null;

  // ------- Render ----------------------------------------------------------

  const css = `
    .mono { font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace; font-variant-numeric: tabular-nums; }
    .pulse-dot { animation: od-pulse 1.4s ease-in-out infinite; }
    @keyframes od-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.7)} }
    .new-bar { animation: od-bar 1.4s ease-in-out infinite; }
    @keyframes od-bar { 0%,100%{opacity:1} 50%{opacity:.5} }
    .order-row:focus-visible { outline: 2px solid #dc2626; outline-offset: -2px; }
    @media (prefers-reduced-motion: reduce){ .pulse-dot,.new-bar{animation:none} }
  `;

  const headerNewBadge = counts.newToday > 0 && (
    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
      <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-white" />
      {counts.newToday} new
    </span>
  );

  // Determine if there are paid/unprinted orders to surface a strong
  // "printer offline" red banner — kitchen MUST notice when this happens.
  const paidUnprinted = orders.filter(
    (o) =>
      (o.payment_status === 'PAID' || o.payment_status === 'COD_PENDING') &&
      (!o.print_status || o.print_status === 'NOT_PRINTED')
  );
  // Retired banner: QZ Tray offline path is no longer used — the built-in
  // POS printer handles its own availability probe on every attempt.
  const showOfflineBanner = false;

  const handlePrinterRetry = async () => {
    toast.info('Reconnecting to printer…');
  };

  return (
    <AdminMobileShell
      title={`Orders${counts.newToday > 0 ? ` · ${counts.newToday} new` : ''}`}
      backTo="/admin/dashboard"
      onRefresh={fetchOrders}
      showPrinterStatus
      newOrderCount={counts.newToday}
    >
      <style>{css}</style>

      {/* Persistent red banner when printer is offline AND there are unprinted
          paid orders — kitchen / staff must know immediately. */}
      {showOfflineBanner && (
        <div
          data-testid="printer-offline-banner"
          className="sticky top-0 z-20 flex items-center gap-2 bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow lg:top-16"
        >
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="flex-1">
            Printer offline — {paidUnprinted.length} order{paidUnprinted.length === 1 ? '' : 's'} waiting to print.
          </span>
          <button
            type="button"
            onClick={handlePrinterRetry}
            className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wide hover:bg-white/25"
            data-testid="printer-retry-button"
          >
            Retry
          </button>
        </div>
      )}

      <AdminPageContainer>
        {/* Desktop-only top bar (hidden on mobile because AdminMobileShell
            already provides title + refresh) */}
        <div className="mb-4 hidden flex-wrap items-center justify-between gap-3 lg:flex">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                Dashboard
              </Button>
            </Link>
            <h1 className="flex items-center text-lg font-bold text-stone-900">
              Orders
              {headerNewBadge}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <PrintQueueBadge printerSettings={printerSettings} />

            <Link to="/admin/printer-settings">
              <Button variant="outline" size="sm" data-testid="printer-settings-link">
                <Printer className="mr-1.5 h-4 w-4" />
                Settings
              </Button>
            </Link>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                exportOrdersToCSV(
                  visibleOrders,
                  `orders_${tab}_${new Date().toISOString().split('T')[0]}.csv`
                );
                toast.success(`Exported ${visibleOrders.length} orders`);
              }}
              disabled={visibleOrders.length === 0}
              data-testid="export-csv-button"
            >
              <Download className="mr-1.5 h-4 w-4" />
              CSV
            </Button>

            <Button
              variant="outline"
              size="icon"
              onClick={fetchOrders}
              className="rounded-full"
              data-testid="refresh-orders-button"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Master-detail */}
        <div
          className="grid gap-4 rounded-2xl border border-stone-200 bg-white overflow-hidden"
          style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}
        >
          <div className="grid lg:grid-cols-[3fr_2fr]" style={{ minHeight: '70vh' }}>
            {/* LEFT: tabs + list */}
            <div
              className={`flex flex-col border-stone-200 ${
                selected ? 'hidden lg:flex' : 'flex'
              } lg:border-r`}
            >
              {/* Tabs */}
              <div className="flex items-center gap-0 border-b border-stone-200 bg-white px-2 pt-2">
                {TABS.map((t) => {
                  const isActive = tab === t.key;
                  const count = counts[t.key];
                  const isToday = t.key === 'today';
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setTab(t.key)}
                      data-testid={`order-tab-${t.key}`}
                      className={`relative flex-1 px-3 py-2.5 text-sm font-semibold transition-colors ${
                        isActive
                          ? 'text-stone-900'
                          : 'text-stone-500 hover:text-stone-700'
                      }`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {t.label}
                        <span
                          className={`mono inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] ${
                            isActive
                              ? 'bg-stone-900 text-white'
                              : 'bg-stone-100 text-stone-600'
                          }`}
                        >
                          {count}
                        </span>
                        {isToday && counts.newToday > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-white" />
                            {counts.newToday}
                          </span>
                        )}
                      </span>
                      {isActive && (
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-red-600" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto bg-stone-50 p-3">
                {loading ? (
                  <p className="py-10 text-center text-sm text-stone-500">
                    Loading orders…
                  </p>
                ) : visibleOrders.length === 0 ? (
                  <div className="py-10 text-center text-sm text-stone-500">
                    No orders in {TABS.find((t) => t.key === tab)?.label.toLowerCase()}.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {visibleOrders.map((order) => (
                      <OrderRow
                        key={order.id}
                        order={order}
                        selected={order.id === selectedId}
                        isNew={isUnopenedNew(order)}
                        onOpen={openOrder}
                        onPrint={handleQZPrint}
                        printing={printing}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: detail */}
            <div className={`${selected ? 'flex' : 'hidden lg:flex'} flex-col`}>
              {selected ? (
                <OrderDetail
                  order={selected}
                  currentStatus={selected.order_status}
                  statusUpdating={updating === selected.id}
                  onStatus={(s) => updateOrderStatus(selected.id, s)}
                  onPrint={handleQZPrint}
                  printing={printing}
                  printerConfigured={!!printerSettings?.printer_name}
                  onResendEmail={handleResendEmail}
                  resendingEmail={resendingEmail}
                  onMarkPaid={handleMarkCodPaid}
                  markingPaid={markingPaid}
                  onRefund={setRefundTarget}
                  onDelete={handleDeleteOrder}
                  onClose={() => setSelectedId(null)}
                />
              ) : (
                <EmptyDetail />
              )}
            </div>
          </div>
        </div>
      </AdminPageContainer>

      {/* Refund modal — unchanged */}
      {refundTarget && (
        <RefundModal
          order={refundTarget}
          isSubmitting={refunding === refundTarget.id}
          onClose={() => setRefundTarget(null)}
          onSubmit={(payload) => handleRefundOrder(refundTarget, payload)}
        />
      )}

      {/* Hidden anchor that future enhancements can target. */}
      <span ref={printedOrdersRef} aria-hidden hidden />
    </AdminMobileShell>
  );
};

/**
 * Modal dialog for issuing a refund against a single order. (Unchanged.)
 */
const RefundModal = ({ order, isSubmitting, onClose, onSubmit }) => {
  const total = Number(order.total_amount || 0);
  const refunded = Number(order.refunded_amount || 0);
  const refundable = Math.max(0, Number((total - refunded).toFixed(2)));
  const [amount, setAmount] = useState(refundable.toFixed(2));
  const [reason, setReason] = useState('');
  const [passcode, setPasscode] = useState('');
  const [passcodeRequired, setPasscodeRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API_URL}/api/admin/refund-passcode`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPasscodeRequired(!!r.data.configured);
      } catch (e) {
        if (!cancelled) setPasscodeRequired(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const method = (order.payment_method || 'card').toLowerCase();
  const isCash = method === 'cod' || method === 'coc';
  const orderNum = orderNumberLabel(order);

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (Number.isNaN(num) || num <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (num > refundable + 0.005) {
      toast.error(`Maximum refundable is £${refundable.toFixed(2)}`);
      return;
    }
    if (passcodeRequired && !/^\d{4}$/.test(passcode)) {
      toast.error('Enter the 4-digit refund passcode');
      return;
    }
    onSubmit({
      amount: num,
      reason: reason.trim() || undefined,
      passcode: passcode || undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
      data-testid="refund-modal"
    >
      <div
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold">Refund order {orderNum}</h3>
            <p className="text-sm text-muted-foreground">
              {order.customer_name} — £{total.toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={`text-xs rounded-md px-3 py-2 mb-4 ${
          isCash ? 'bg-amber-50 text-amber-800' : 'bg-blue-50 text-blue-800'
        }`}>
          {isCash
            ? 'Cash order — this is a bookkeeping refund. No card will be charged back. Hand the cash to the customer separately.'
            : 'Card order — this will trigger an automatic Stripe refund to the customer\'s original payment method (3–10 business days).'}
        </div>

        {refunded > 0 && (
          <div className="text-xs text-muted-foreground mb-3">
            Already refunded: £{refunded.toFixed(2)} · Remaining refundable: £{refundable.toFixed(2)}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium" htmlFor="refund-amount">Amount (£)</label>
            <Input
              id="refund-amount"
              type="number"
              step="0.01"
              min="0.01"
              max={refundable}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1.5"
              data-testid="refund-amount-input"
              required
            />
            <div className="mt-2 flex gap-2">
              <Button type="button" size="sm" variant="outline" onClick={() => setAmount(refundable.toFixed(2))}>
                Full
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setAmount((refundable / 2).toFixed(2))}>
                Half
              </Button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium" htmlFor="refund-reason">Reason (optional)</label>
            <Input
              id="refund-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. wrong item delivered"
              className="mt-1.5"
              data-testid="refund-reason-input"
              maxLength={500}
            />
          </div>

          {passcodeRequired && (
            <div>
              <label className="text-sm font-medium" htmlFor="refund-passcode">
                Refund passcode (4 digits)
              </label>
              <Input
                id="refund-passcode"
                type="password"
                inputMode="numeric"
                pattern="[0-9]{4}"
                maxLength={4}
                autoComplete="off"
                value={passcode}
                onChange={(e) => {
                  const clean = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setPasscode(clean);
                }}
                placeholder="••••"
                className="mt-1.5 tracking-widest text-center text-lg"
                data-testid="refund-passcode-confirm-input"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                4-digit pin configured in Admin → Settings → Refund Passcode.
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={isSubmitting}
              data-testid="refund-confirm-button"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Refunding…
                </>
              ) : (
                <>Refund £{(parseFloat(amount) || 0).toFixed(2)}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminOrders;
